import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MeasurementSessionStatus,
  TERMINAL_SESSION_STATUSES,
  type MeasurementSessionStatus as SessionStatus,
} from '@optiflow/types';
import { Alert } from '@/components/ui/Alert';
import { Button } from '@/components/ui/Button';
import { LoadingState } from '@/components/ui/LoadingState';
import { Modal } from '@/components/ui/Modal';
import {
  useCancelMeasurementSessionMutation,
  useMeasurementSessionQuery,
  useRetakeMeasurementSessionMutation,
} from '@/modules/measurements/api/measurements.api';
import { apiErrorMessage } from '@/services/apiClient';
import {
  useCreateFaceSessionMutation,
  useStartFaceSessionMutation,
  useSubmitFaceCandidateMutation,
} from '../api/face.api';
import { candidateFrom } from '../submitFaceCandidate';
import type { FaceAttempt } from '../vision/useFaceCapture';
import { FaceReviewPanel } from './FaceReviewPanel';

// The analysis engine and its MediaPipe assets are megabytes; they load when somebody analyses a face.
const FaceAnalysisCapture = lazy(() => import('./FaceAnalysisCapture'));
const FacePhotoAnalysis = lazy(() => import('./FacePhotoAnalysis'));

/**
 * Analysing a face shape on this device, end to end — from its camera or from a photo.
 *
 * A FACE session is created and started, the image is read locally, its suggestion is offered to the
 * session, and the same dialog then shows the review where a person decides. The session reaches
 * COMPLETED only when that person has decided — a suggestion alone never completes it.
 *
 * The camera and the photo differ only in how the image arrives, so they share everything after that:
 * the session, the binding that stops a result reaching the wrong session, the review, and the rule
 * that a confirmed session is never asked to cancel. The session records which one it was.
 */
export function FaceCameraDialog({
  customerId,
  isOpen,
  onClose,
  onEnterManually,
  input = 'CAMERA',
}: {
  customerId: string;
  isOpen: boolean;
  onClose: () => void;
  onEnterManually?: () => void;
  input?: 'CAMERA' | 'PHOTO';
}) {
  const { t } = useTranslation();

  const create = useCreateFaceSessionMutation();
  const start = useStartFaceSessionMutation();
  const submit = useSubmitFaceCandidateMutation();
  const retake = useRetakeMeasurementSessionMutation();
  const cancel = useCancelMeasurementSessionMutation();

  const [sessionId, setSessionId] = useState<string | null>(null);
  // The session the capture may use: set only once it has been started, for this opening of the dialog.
  const [readySessionId, setReadySessionId] = useState<string | null>(null);
  const [delivery, setDelivery] = useState<{ state: 'idle' | 'sending' | 'sent' | 'failed' }>({
    state: 'idle',
  });
  const [error, setError] = useState<string | null>(null);
  const started = useRef(false);
  // Which opening of the dialog a request belongs to. An answer for an opening that has since closed is not this one's.
  const opening = useRef(0);
  // Sessions this dialog has already asked to cancel; a second request for the same one is refused by the server.
  const ended = useRef(new Set<string>());

  const end = (id: string) => {
    if (ended.current.has(id)) return;
    ended.current.add(id);
    cancel.mutate(id);
  };

  const session = useMeasurementSessionQuery(sessionId, false);
  const status = session.data?.status ?? null;

  useEffect(() => {
    // Effects run twice in development; one dialog opening is one session.
    if (!isOpen || started.current) return;
    started.current = true;
    const thisOpening = ++opening.current;
    const stillOpen = () => opening.current === thisOpening;

    // mutateAsync, not mutate: a call's own callbacks are dropped once a later opening calls again, and a late answer must still be seen so its session is ended.
    const begin = async () => {
      const created = await create.mutateAsync({
        customerId,
        requestedSource: input === 'PHOTO' ? 'PHOTO' : 'LIVE_CAMERA',
        opening: thisOpening,
      });
      // Closed while the session was being made: end it rather than start one nobody is watching.
      if (!stillOpen()) {
        end(created.id);
        return;
      }
      setSessionId(created.id);

      const running = await start.mutateAsync(created.id);
      if (!stillOpen()) {
        end(running.id);
        return;
      }
      // Only now can a capture belong to it: the session exists and will take a suggestion.
      setReadySessionId(running.id);
    };

    begin().catch((caught: unknown) => {
      if (stillOpen()) setError(apiErrorMessage(caught) ?? t('face.errors.record'));
    });
    // The mutations are stable for the dialog's life; only opening it should start a session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, customerId]);

  /**
   * Closing, and what it means for the session.
   *
   * Before a candidate, and while one is waiting for review, closing ends the session — a capture
   * nobody is watching should not stay open. After a confirmation it must not: the session is already
   * COMPLETED, and asking to cancel it is a request the server is right to refuse.
   */
  const close = (known?: SessionStatus) => {
    const effective = known ?? status;
    // A session whose status has not been read yet was only just made, so it is still open and is ended too.
    if (sessionId && (effective === null || !TERMINAL_SESSION_STATUSES.includes(effective))) {
      end(sessionId);
    }
    opening.current += 1;
    started.current = false;
    setSessionId(null);
    setReadySessionId(null);
    setDelivery({ state: 'idle' });
    setError(null);
    onClose();
  };

  /**
   * Offer what this device suggested, for the session it was captured under.
   *
   * A capture that settled on nothing offers nothing, and a capture belonging to another session
   * offers nothing either — `candidateFrom` refuses both, so neither reaches the network.
   */
  const deliver = (attempt: FaceAttempt) => {
    if (!readySessionId) return;
    const candidate = candidateFrom(attempt, readySessionId);
    if (!candidate) return;

    setError(null);
    setDelivery({ state: 'sending' });
    submit.mutate(
      { sessionId: readySessionId, candidate },
      {
        onSuccess: () => {
          setDelivery({ state: 'sent' });
          void session.refetch();
        },
        onError: (caught) => {
          setDelivery({ state: 'failed' });
          setError(apiErrorMessage(caught) ?? t('face.capture.sendFailed'));
        },
      },
    );
  };

  const underReview =
    status === MeasurementSessionStatus.AWAITING_REVIEW && session.data?.faceCandidate
      ? session.data.faceCandidate
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={() => close()}
      title={
        underReview
          ? t('face.review.title')
          : input === 'PHOTO'
            ? t('face.photo.title')
            : t('face.capture.title')
      }
      description={underReview ? t('face.review.suggestionOnly') : t('face.capture.staffNote')}
      footer={
        <Button variant="secondary" onClick={() => close()}>
          {status && !TERMINAL_SESSION_STATUSES.includes(status)
            ? t('common.cancel')
            : t('common.close')}
        </Button>
      }
    >
      <div className="space-y-4">
        {error ? <Alert tone="error">{error}</Alert> : null}
        {session.data?.isValidation ? (
          <Alert tone="warning">{t('face.validationMode')}</Alert>
        ) : null}

        {underReview && sessionId ? (
          <FaceReviewPanel
            sessionId={sessionId}
            candidate={underReview}
            // The confirmation's own answer says the session is finished, so closing does not cancel it.
            onDone={(completed) => close(completed.status)}
            onRetake={() => {
              setDelivery({ state: 'idle' });
              retake.mutate(sessionId, {
                onSuccess: () => void session.refetch(),
                onError: (caught) => setError(apiErrorMessage(caught) ?? t('face.errors.record')),
              });
            }}
          />
        ) : (
          <div className="flex justify-center">
            <Suspense fallback={<LoadingState />}>
              {input === 'PHOTO' ? (
                <FacePhotoAnalysis
                  sessionKey={readySessionId}
                  onResult={deliver}
                  delivery={delivery}
                  onEnterManually={onEnterManually}
                  isValidation={session.data?.isValidation ?? false}
                />
              ) : (
                <FaceAnalysisCapture
                  sessionKey={readySessionId}
                  onResult={deliver}
                  delivery={delivery}
                  onEnterManually={onEnterManually}
                  isValidation={session.data?.isValidation ?? false}
                />
              )}
            </Suspense>
          </div>
        )}
      </div>
    </Modal>
  );
}
